import { useLazyGetAccountHolderQuery } from "@/modules/account/services";
import { AttachmentType } from "@/modules/beneficiary/common/enum";
import type {
  IAttachmentInfo,
  TBeneficiaryAttachmentItem
} from "@/modules/beneficiary/common/types";
import { useAttachmentValidationMutation } from "@/modules/beneficiary/services";
import {
  accountAttachmentValidationSchema,
  cardAttachmentValidationSchema
} from "@/modules/beneficiary/validations";
import { useLazyGetCardHolderQuery } from "@/modules/card/services";
import { Notification } from "@/shared/components";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { TFormData } from "@ab.uitools/ui-library/types/globalTypes";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AttachForm } from "./AttachForm";

interface Props {
  data?: {
    note?: string;
    number?: string;
  } | null;
  clientId?: string;
  beneficiaryId?: string;
  setClientId: Dispatch<SetStateAction<string>>;
  cards: TBeneficiaryAttachmentItem[];
  accounts: TBeneficiaryAttachmentItem[];
  isEdit?: boolean;
  onConfirm: (data: TBeneficiaryAttachmentItem) => void;
  isOpen: boolean;
  onClose: () => void;
  attachmentType?: AttachmentType | null;
  allowClientChange?: boolean;
}

export const AttachModal = ({
  data,
  cards,
  isOpen,
  isEdit,
  onClose,
  accounts,
  clientId,
  setClientId,
  onConfirm,
  beneficiaryId,
  attachmentType,
  allowClientChange,
}: Props) => {
  const { t } = useTranslation();
  const [
    getAccountHolder,
    {
      isFetching: isFetchingAccountHolder,
      error: accountHolderApiError,
    },
  ] = useLazyGetAccountHolderQuery();
  const [
    getCardHolder,
    {
      isFetching: isFetchingCardHolder,
      error: cardHolderApiError,
    }
  ] = useLazyGetCardHolderQuery();
  const [
    validateAttachment,
    {
      isLoading: isValidationLoading,
      data: attachmentValidation,
      error: attachmentValidationApiError,
    },
  ] = useAttachmentValidationMutation();
  const [info, setInfo] = useState<IAttachmentInfo | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(!isEdit);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);
  const [hasDifferentClientError, setHasDifferentClientError] = useState<boolean>(false);
  const [hasApiError, setHasApiError] = useState<boolean>(false);

  useEffect(() => {
    setHasApiError(!!(accountHolderApiError || cardHolderApiError || attachmentValidationApiError));
  }, [accountHolderApiError, cardHolderApiError, attachmentValidationApiError]);

  useEffect(() => {
    if (isOpen) {
      setIsDisabled(!isEdit);
      setHasFormChanges(false);
      setHasApiError(false);
    }
  }, [isOpen]);

  const handleSubmit = (data: TFormData, isDirty: boolean) => {
    const newItem = {
      ...data,
      network: info?.network,
      clientId: info?.clientId,
    } as TBeneficiaryAttachmentItem;
    if (isDirty) {
      onConfirm(newItem);
    }
  };

  const handleClose = () => {
    onClose();
    setInfo(null);
    setHasDifferentClientError(false);
    setHasApiError(false);
  };

  const isAccountAttachment = attachmentType === AttachmentType.Account;

  const existingNumbers = useMemo(() => {
    const list = isAccountAttachment ? accounts : cards;
    return list
      .filter((item) => item.number !== data?.number)
      .map((item) => item.number);
  }, [isAccountAttachment, accounts, cards, data?.number]);

  const cardAttachmentTitleKey = isEdit ?
    "beneficiary.cardAttachment.editTitle" :
    "beneficiary.cardAttachment.title";

  const accountAttachmentTitleKey = isEdit ?
    "beneficiary.accountAttachment.editTitle" :
    "beneficiary.accountAttachment.title";

  const titleKey = isAccountAttachment ?
    accountAttachmentTitleKey :
    cardAttachmentTitleKey;

  const schemaProps = {
    t,
    existingNumbers,
    isDuplicate: attachmentValidation?.hasDuplicateEntry,
    hasSameClientCode: attachmentValidation?.hasBeneficiaryWithSameEntryClientCode,
    hasDifferentClient: hasDifferentClientError,
  };

  const validationSchema = isAccountAttachment ?
    accountAttachmentValidationSchema(schemaProps) :
    cardAttachmentValidationSchema(schemaProps);

  return (
    <Modal
      closeIcon
      closeOnOutsideClick={false}
      titleProps={{
        title: t(titleKey),
        size: "large"
      }}
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={() => {}}
      buttonProps={{
        confirm: {
          formId: "beneficiary-attachment-form",
          buttonText: isEdit ? t("general.save") : t("general.attach"),
          buttonActionType: "submit",
          isLoading: isFetchingAccountHolder ||
            isFetchingCardHolder ||
            isValidationLoading,
          disabled: isDisabled || (isEdit && !hasFormChanges) || hasApiError,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
    >
      <Notification className="mb-8"/>
      <FormContainer
        mode="onBlur"
        reValidateMode="onBlur"
        className="full-width"
        formId="beneficiary-attachment-form"
        initialValues={{ ...data }}
        onSubmit={(data, formState) => {
          if (!hasApiError && !formState.isSubmitted) {
            handleSubmit(data, formState.isDirty);
          }
        }}
        validationScheme={validationSchema}
      >
        <AttachForm
          data={data}
          info={info}
          isEdit={isEdit}
          setInfo={setInfo}
          clientId={clientId}
          beneficiaryId={beneficiaryId}
          setClientId={setClientId}
          getCardHolder={getCardHolder}
          attachmentType={attachmentType}
          getAccountHolder={getAccountHolder}
          validateAttachment={validateAttachment}
          isAccountAttachment={isAccountAttachment}
          attachmentValidation={attachmentValidation}
          hasApiError={hasApiError}
          setIsDisabled={setIsDisabled}
          onDifferentClientError={setHasDifferentClientError}
          allowClientChange={allowClientChange}
          onFormChange={(isDirty) => setHasFormChanges(isDirty)}
        />
      </FormContainer>
    </Modal>
  );
};
